import { FC, useEffect, useMemo, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { BadgeBanner } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  StickyPromotionWrapper,
  StickyPromotionWrapperProps,
} from '../../../common/components/sticky-promotion-wrapper';
import { useComponentEngagementStore } from '../../../common/store/component-engagement-store';

const DEFAULT_COMPONENT_ID = 'call-intel-widget:patient-profile';

const CallIntelPatientProfilePromoBanner: FC = () => {
  const { t } = useTranslation('highValueAdoption', { keyPrefix: 'call-intel' });

  return (
    <BadgeBanner>
      <BadgeBanner.AnimatedBadge label={t('New')} />
      <BadgeBanner.Title>{t('Call Intelligence insights are now in the Patient Profile!')}</BadgeBanner.Title>
    </BadgeBanner>
  );
};

interface CallIntelStickyPromoWrapperProps extends Pick<StickyPromotionWrapperProps, 'children'> {
  enabled?: boolean;
  componentId?: string;
  maxVisibilityCount?: number;
  maxInteractionCount?: number;
}

/**
 * Wrapper component that displays a sticky promotion banner for Call Intelligence features
 * @param children - React child components to be rendered
 * @param componentId - Optional ID for the component tracking (defaults to 'call-intel-widget:patient-profile')
 * @param promoFeatureFlag - feature flag to control the visibility of the promo
 * @param maxVisibilityCount - Maximum number of times the promo should be visible (defaults to 2)
 * @param maxInteractionCount - Maximum number of times the user can interact with the promo (defaults to 1)
 * @returns React component
 */
export const CallIntelStickyPromoWrapper: FC<CallIntelStickyPromoWrapperProps> = ({
  children,
  componentId = DEFAULT_COMPONENT_ID,
  enabled = false,
  maxInteractionCount = 1,
  maxVisibilityCount = 2,
}) => {
  const { getEngagementData, trackVisibility, trackInteraction } = useComponentEngagementStore();

  const persistPromoVisibleRef = useRef(false);

  const { ref, inView } = useInView({
    triggerOnce: true,
  });

  const shouldShowPromo = useMemo(() => {
    // If the promo is already visible, return true
    if (persistPromoVisibleRef.current) {
      return true;
    }

    const { visibilityCount, interactionCount } = getEngagementData({
      componentId,
    });

    const shouldShowPromo = interactionCount < maxInteractionCount && visibilityCount < maxVisibilityCount;

    // Persist the promo visibility state
    persistPromoVisibleRef.current ||= shouldShowPromo;

    return shouldShowPromo;
  }, [getEngagementData, componentId, maxInteractionCount, maxVisibilityCount]);

  useEffect(() => {
    if (inView) {
      trackVisibility({ componentId });
    }
  }, [inView, componentId]);

  return (
    <StickyPromotionWrapper
      promoComponent={
        <div ref={ref}>
          <CallIntelPatientProfilePromoBanner />
        </div>
      }
      showPromo={enabled && shouldShowPromo}
      scrollButtonProps={{
        trackingId: `${componentId}:scroll-button`,
        onClick: () => trackInteraction({ componentId }),
      }}
      styles={{
        bottom: `-${theme.spacing(2)}`,
      }}
    >
      {children}
    </StickyPromotionWrapper>
  );
};
